import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { Points, PointsMaterial, BufferGeometry, Float32BufferAttribute } from 'three';

export default class Bus {
    constructor(scene, physics, inputController) {
        this.scene = scene;
        this.physics = physics;
        this.inputController = inputController;

        // Loading state
        this.isLoading = true;
        this.loadingPromise = null;

        // Bus properties
        this.width = 2.5;
        this.height = 3;
        this.length = 12;
        this.wheelRadius = 0.5;
        this.wheelWidth = 0.4;

        // Weapon mount points
        this.weaponMountPoints = {
            top: new THREE.Vector3(0, this.height / 2 + 0.2, -this.length / 4),
            middle: new THREE.Vector3(0, this.height / 2 + 0.3, 0),
            rear: new THREE.Vector3(0, 0.3, this.length / 2 - 0.5)
        };

        // Physics body
        this.physicsBody = null;
        this.vehicle = null;

        // Movement properties
        this.throttle = 0;
        this.brake = 0;
        this.steering = 0;
        this.maxSteeringAngle = 0.7;  // Increased for more responsive turning
        this.enginePower = 300;       // Increased for faster acceleration
        this.brakePower = 200;         // Increased for stronger braking
        this.reverseEnginePower = 80; // Increased reverse power
        this.maxSpeed = 96;           // 83 m/s is approximately 300 km/h for exciting speed
        this.turnSpeed = 2.0;         // Increased for faster turning

        // Coins properties
        this.coins = 0;               // Player's coin count
        this.coinCollectionRadius = 3; // Distance to collect coins

        // Fuel properties
        this.fuelCapacity = 100;      // Maximum fuel capacity in liters
        this.fuel = this.fuelCapacity * 0.30; // Current fuel level, start with 30% tank
        this.fuelConsumptionRate = {
            idle: 0.8,                // Increased from 0.2 to 0.8 liters per minute when idle
            driving: 2.0,             // Increased from 0.5 to 2.0 liters per minute when driving
            speedFactor: 0.05         // Increased from 0.015 to 0.05 additional consumption per km/h 
        };
        this.fuelLastUpdate = performance.now(); // Track time for fuel consumption
        this.engineStarted = false;   // Track if engine is running
        this.lastInputTime = null;    // Track when the last input occurred

        // Smooth acceleration and braking
        this.accelerationRate = 0.15;  // More responsive acceleration
        this.brakeRate = 0.5;          // Snappy braking response
        this.targetThrottle = 0;      // Target throttle for smooth acceleration
        this.targetBrake = 0;         // Target brake for smooth braking

        // Current road data
        this.currentRoad = null;
        this.currentSpeedLimits = {
            forward: 100,  // Changed from 60 to match our maxSpeed of 100 km/h
            backward: 20,  // Reduced from 30
            corner: 30     // Reduced from 40
        };

        // Wheel state
        this.wheelsOnGround = [false, false, false, false];

        // Terrain slope alignment
        this._targetSlopeQuaternion = new THREE.Quaternion();
        this._currentSlopeQuaternion = new THREE.Quaternion();
        this._slopeLerpFactor = 0.08; // How quickly bus aligns to terrain (0-1, lower = smoother)

        // Free drive mode
        this.freeDriveMode = true;    // Start with free drive enabled
        this.terrainMode = "all";     // all, road, or terrain

        // Debug
        this.debug = false;
        this.lastTPressed = false;
        this.debugInfo = document.createElement('div');
        this.speedDisplay = null;
        this.velocity = new THREE.Vector3(); // Store current velocity

        // Model and mesh
        this.mesh = null;
        this.model = null;
        this.wheels = [];

        // Exhaust particle system
        this.exhaustParticles = null;
        this.particleSystem = null;
        this.particleCount = 100;
        this.particles = new Float32Array(this.particleCount * 3);
        this.particleVelocities = new Float32Array(this.particleCount * 3);
        this.particleLifetimes = new Float32Array(this.particleCount);
        this.exhaustPosition = new THREE.Vector3(-0.5, 0.5, -this.length / 2); // Position relative to bus

        // Attempt to create the bus immediately, but allow for retry
        this.createBus();

        // If physics isn't ready yet, retry after a short delay
        if (!this.physicsBody) {
            this.retryCreation();
        }

    }

    retryCreation() {
        // If physics is not initialized yet, retry after a delay
        //console.log("Physics not ready, scheduling retry for bus creation");
        setTimeout(() => {
            if (!this.physicsBody) {
                //console.log("Retrying bus creation...");
                this.createBus();

                // If still not successful, try again
                if (!this.physicsBody) {
                    this.retryCreation();
                } else {
                    console.log("Bus created successfully on retry");
                }
            }
        }, 500);
    }

    async createBus() {
        //console.log("Creating bus...");
        if (!this.physics.isInitialized) {
            console.log("Physics not initialized yet");
            return;
        }

        //console.log("Physics is initialized, creating bus");

        // Create physics body first
        this.createPhysicsBody();

        // Then load the GLTF model which will adjust to match the physics
        try {
            await this.loadBusModel();
        } catch (error) {
            console.error("Failed to load bus model:", error);
            // Fallback to custom bus if model loading fails
            this.createCustomBus();
        }

        // Create debug display
        this.createDebugDisplay();
    }

    createPhysicsBody() {
        try {
            // Create physics body with standard bus dimensions
            const chassisHalfExtents = new THREE.Vector3(
                this.width / 2,
                this.height / 2,
                this.length / 2
            );

            // Position the chassis just above the ground
            const chassisPosition = new THREE.Vector3(0, chassisHalfExtents.y + 0.1, 0);

            //console.log(`Creating vehicle physics at position (${chassisPosition.x}, ${chassisPosition.y}, ${chassisPosition.z})`);
            //console.log(`Chassis half-extents: (${chassisHalfExtents.x}, ${chassisHalfExtents.y}, ${chassisHalfExtents.z})`);

            // Create the vehicle
            this.vehicle = this.physics.createVehicle(chassisPosition, chassisHalfExtents);

            if (!this.vehicle) {
                throw new Error("Failed to create vehicle physics");
            }

            // Store the chassis body
            this.physicsBody = this.vehicle.chassis;

            // Store reference to the bus in the physics body for collision callbacks
            this.physicsBody.bus = this;

            // Apply physics properties
            if (!this.physics.usingFallbackPhysics) {
                // Set linear damping to simulate air resistance
                this.physicsBody.setLinearDamping(0.1);

                // Set angular damping to prevent excessive rotation
                this.physicsBody.setAngularDamping(0.8); // Increased to reduce wobble

                // Set max angular velocity to prevent unrealistic spinning
                this.physicsBody.setMaxAngularVelocity(4.0); // Reduced for more stability

                // Enable Continuous Collision Detection for fast-moving objects
                this.physicsBody.setCCD(true, 0.01);
            }

            //console.log("Bus physics created successfully");
            return true;
        } catch (error) {
            //console.error("Error creating bus physics:", error);

            // If we hit an error, we'll try using a simple body
            if (!this.physics.usingFallbackPhysics) {
                //console.warn("Switching to fallback physics for bus");
                this.physics.usingFallbackPhysics = true;
                return this.createPhysicsBody();
            }
            return false;
        }
    }

    loadBusModel() {
        // Load a GLTF model for the bus
        const loader = new GLTFLoader();
        //const modelPath = '/models/bus/scene.gltf';
        const modelPath = '/models/bus/bus.glb';

        this.isLoading = true;
        this.loadingPromise = new Promise((resolve, reject) => {
            loader.load(
                modelPath,
                (glb) => {
                    try {
                        // Create a new group to hold the model
                        this.model = new THREE.Group();

                        // Add the loaded model to our group
                        this.model.add(glb.scene);

                        // Initial scale - we'll adjust after measuring
                        this.model.scale.set(0.1, 0.1, 0.1);

                        // Position and rotate the model
                        this.model.position.set(0, 0, 0);
                        this.model.rotation.y = Math.PI; // Make it face forward (-Z direction)

                        // Set up shadows
                        glb.scene.traverse((node) => {
                            if (node.isMesh) {
                                node.castShadow = true;
                                node.receiveShadow = true;
                            }
                        });

                        // First center the model
                        this.centerModel(this.model);

                        // Then adjust size to match physics dimensions
                        this.adjustModelSize();

                        // Replace any existing mesh with the loaded model
                        if (this.mesh) {
                            this.scene.remove(this.mesh);
                        }

                        // Set the model as the bus mesh
                        this.mesh = this.model;
                        this.scene.add(this.mesh);

                        // Create weapon mount points on the model
                        this.mesh.weaponMountPoints = {
                            top: new THREE.Vector3(0, this.height / 2 + 0.2, -this.length / 4),
                            middle: new THREE.Vector3(0, this.height / 2 + 0.3, 0),
                            rear: new THREE.Vector3(0, 0.3, this.length / 2 - 0.5)
                        };

                        // Find wheels in the model
                        this.findWheelsInModel();

                        // Associate with physics body for fallback physics
                        if (this.physics.usingFallbackPhysics && this.physicsBody) {
                            this.physicsBody.threeMesh = this.mesh;
                        }

                        // Final ground contact adjustment
                        this.ensureGroundContact();

                        this.isLoading = false;
                        resolve();
                    } catch (error) {
                        this.isLoading = false;
                        reject(error);
                    }
                },
                (xhr) => {
                    // Loading progress
                    const percent = (xhr.loaded / xhr.total) * 100;
                    // You can emit a loading progress event here if needed
                },
                (error) => {
                    // Error handling
                    this.isLoading = false;
                    this.createCustomBus();
                    reject(error);
                }
            );
        });

        return this.loadingPromise;
    }

    centerModel(model) {
        // Create a temporary bounding box
        const boundingBox = new THREE.Box3().setFromObject(model);
        const center = boundingBox.getCenter(new THREE.Vector3());
        const size = boundingBox.getSize(new THREE.Vector3());

        // Log the initial size
        //console.log(`Initial model size: ${size.x.toFixed(2)} x ${size.y.toFixed(2)} x ${size.z.toFixed(2)}`);

        // Adjust model position to center horizontally
        model.position.x = model.position.x - center.x;
        model.position.z = model.position.z - center.z;

        // Store the model's dimensions for later use
        this.modelHeight = size.y;

        //console.log(`Model centered at (${model.position.x.toFixed(2)}, ${model.position.y.toFixed(2)}, ${model.position.z.toFixed(2)})`);
    }

    adjustModelSize() {
        if (!this.model) return;

        // Get current model size
        const boundingBox = new THREE.Box3().setFromObject(this.model);
        const size = boundingBox.getSize(new THREE.Vector3());

        // Calculate scale factor to match our desired bus dimensions
        // We'll use the longest dimension (usually length) as the reference
        const currentMaxDimension = Math.max(size.x, size.z);
        const targetMaxDimension = Math.max(this.width, this.length);
        const scaleFactor = targetMaxDimension / currentMaxDimension;

        // Apply the scale
        this.model.scale.multiplyScalar(scaleFactor);

        //console.log(`Adjusted model scale to ${scaleFactor.toFixed(4)} to match target dimensions`);

        // Update bounding box after scaling
        const newBoundingBox = new THREE.Box3().setFromObject(this.model);
        const newSize = newBoundingBox.getSize(new THREE.Vector3());
        //console.log(`New model size: ${newSize.x.toFixed(2)} x ${newSize.y.toFixed(2)} x ${newSize.z.toFixed(2)}`);
    }

    ensureGroundContact() {
        if (!this.model) return;

        // Get updated bounding box
        const boundingBox = new THREE.Box3().setFromObject(this.model);

        // Calculate necessary Y position to place the bottom of the model on the ground
        const bottomY = boundingBox.min.y;
        const offsetY = -bottomY + 0.1; // 0.1 units above ground for clearance

        // Apply the offset
        this.model.position.y += offsetY;

        //console.log(`Applied Y offset of ${offsetY.toFixed(2)} to ensure ground contact`);
        //console.log(`Final model position: (${this.model.position.x.toFixed(2)}, ${this.model.position.y.toFixed(2)}, ${this.model.position.z.toFixed(2)})`);
    }

    createCustomBus() {
        // Create a group to hold all bus parts - simplified temporary model
        this.mesh = new THREE.Group();
        this.mesh.position.set(0, 2, 0);
        this.mesh.rotation.y = 0; // Align with road direction
        this.scene.add(this.mesh);

        // Bus body dimensions
        const bodyWidth = this.width;
        const bodyHeight = this.height;
        const bodyLength = this.length;

        // Create main bus body - simplified with basic material
        const bodyGeometry = new THREE.BoxGeometry(bodyWidth, bodyHeight, bodyLength);
        const bodyMaterial = new THREE.MeshPhongMaterial({
            color: 0xF7D51D, // School bus yellow
            shininess: 30,
            specular: 0x333333
        });
        const busBody = new THREE.Mesh(bodyGeometry, bodyMaterial);
        busBody.castShadow = true;
        busBody.receiveShadow = true;
        this.mesh.add(busBody);

        // Add black stripe - simplified
        const stripeGeometry = new THREE.BoxGeometry(bodyWidth + 0.05, bodyHeight * 0.08, bodyLength * 0.8);
        const stripeMaterial = new THREE.MeshPhongMaterial({
            color: 0x111111,
            shininess: 20
        });
        const stripe = new THREE.Mesh(stripeGeometry, stripeMaterial);
        stripe.position.set(0, -bodyHeight * 0.2, 0);
        stripe.castShadow = true;
        stripe.receiveShadow = true;
        this.mesh.add(stripe);

        // Create windshield and windows - swap dimensions
        this.addBusWindows();

        // Create hood (front part, slightly lower) - swap dimensions
        const hoodLength = bodyLength * 0.2;
        const hoodGeometry = new THREE.BoxGeometry(bodyWidth, bodyHeight * 0.6, hoodLength);
        const hoodMaterial = new THREE.MeshStandardMaterial({
            color: 0xFFD800, // Same yellow
            roughness: 0.5,
            metalness: 0.2
        });
        const hood = new THREE.Mesh(hoodGeometry, hoodMaterial);
        hood.position.set(0, -bodyHeight * 0.2, bodyLength * 0.4);
        hood.castShadow = true;
        hood.receiveShadow = true;
        this.mesh.add(hood);

        // Create headlights - swap positions
        const headlightGeometry = new THREE.BoxGeometry(0.4, 0.4, 0.2);
        const headlightMaterial = new THREE.MeshStandardMaterial({
            color: 0xFFFFFF,
            roughness: 0.2,
            metalness: 0.8,
            emissive: 0xFFFFAA,
            emissiveIntensity: 0.5
        });

        // Left headlight
        const leftHeadlight = new THREE.Mesh(headlightGeometry, headlightMaterial);
        leftHeadlight.position.set(-bodyWidth * 0.4, -bodyHeight * 0.2, bodyLength * 0.5 - 0.1);
        this.mesh.add(leftHeadlight);

        // Right headlight
        const rightHeadlight = new THREE.Mesh(headlightGeometry, headlightMaterial);
        rightHeadlight.position.set(bodyWidth * 0.4, -bodyHeight * 0.2, bodyLength * 0.5 - 0.1);
        this.mesh.add(rightHeadlight);

        // Create taillights - swap positions
        const taillightMaterial = new THREE.MeshStandardMaterial({
            color: 0xFF0000,
            roughness: 0.2,
            metalness: 0.5,
            emissive: 0xFF0000,
            emissiveIntensity: 0.3
        });

        // Left taillight
        const leftTaillight = new THREE.Mesh(headlightGeometry, taillightMaterial);
        leftTaillight.position.set(-bodyWidth * 0.4, -bodyHeight * 0.2, -bodyLength * 0.5 + 0.1);
        this.mesh.add(leftTaillight);

        // Right taillight
        const rightTaillight = new THREE.Mesh(headlightGeometry, taillightMaterial);
        rightTaillight.position.set(bodyWidth * 0.4, -bodyHeight * 0.2, -bodyLength * 0.5 + 0.1);
        this.mesh.add(rightTaillight);

        // Create roof - swap dimensions
        const roofGeometry = new THREE.BoxGeometry(bodyWidth, bodyHeight * 0.1, bodyLength);
        const roofMaterial = new THREE.MeshStandardMaterial({
            color: 0x111111, // Black roof
            roughness: 0.7,
            metalness: 0.1
        });
        const roof = new THREE.Mesh(roofGeometry, roofMaterial);
        roof.position.set(0, bodyHeight * 0.5 + 0.05, 0);
        roof.castShadow = true;
        this.mesh.add(roof);

        // Add door - swap dimensions and position
        const doorGeometry = new THREE.BoxGeometry(bodyWidth * 0.3, bodyHeight * 0.7, 0.1);
        const doorMaterial = new THREE.MeshStandardMaterial({
            color: 0xFFD800,
            roughness: 0.5,
            metalness: 0.2
        });
        const door = new THREE.Mesh(doorGeometry, doorMaterial);
        door.position.set(bodyWidth * 0.5 + 0.05, -bodyHeight * 0.1, bodyLength * 0.25);
        this.mesh.add(door);

        // Add door window - swap dimensions
        const doorWindowGeometry = new THREE.BoxGeometry(bodyWidth * 0.25, bodyHeight * 0.3, 0.15);
        const doorWindowMaterial = new THREE.MeshStandardMaterial({
            color: 0x88CCFF,
            transparent: true,
            opacity: 0.6,
            metalness: 0.2,
            roughness: 0.1
        });
        const doorWindow = new THREE.Mesh(doorWindowGeometry, doorWindowMaterial);
        doorWindow.position.set(bodyWidth * 0.5 + 0.05, bodyHeight * 0.2, bodyLength * 0.25);
        this.mesh.add(doorWindow);

        // Create wheels - new positions will be set in createWheels
        this.createWheels();

        // Set this as the model (for compatibility with existing code)
        this.model = this.mesh;

        // Associate the mesh with the physics body for fallback physics
        if (this.physics.usingFallbackPhysics && this.physicsBody) {
            this.physicsBody.threeMesh = this.mesh;
        }
    }

    findWheelsInModel() {
        // Try to find wheels in the loaded model based on naming conventions
        //console.log("Looking for wheels in model...");
        const wheelNames = ['wheel', 'tire', 'tyre', 'wheels', 'rad', 'rueda', 'roue', 'колесо'];

        this.wheels = [];

        // Search more thoroughly through the model hierarchy
        this.model.traverse((child) => {
            // Check for mesh objects that could be wheels
            if (child.isMesh) {
                const nameLower = child.name.toLowerCase();

                // Check for wheel-related names
                let isWheel = false;
                for (const wheelName of wheelNames) {
                    if (nameLower.includes(wheelName)) {
                        //console.log(`Found wheel by name: ${child.name}`);
                        isWheel = true;
                        break;
                    }
                }

                // If not found by name, try to detect by geometry (wheels are usually cylindrical)
                if (!isWheel && child.geometry) {
                    // Check if it's a cylinder-like geometry or has circular features
                    if (child.geometry.type &&
                        (child.geometry.type.includes('Cylinder') ||
                            child.geometry.type.includes('Circle'))) {
                        //console.log(`Found potential wheel by geometry type: ${child.name}`);
                        isWheel = true;
                    }

                    // Additional check based on the object's position (wheels are usually at the bottom sides)
                    const position = new THREE.Vector3();
                    child.getWorldPosition(position);

                    // Calculate position relative to model center
                    const relativeY = position.y - this.model.position.y;

                    // If it's a small object near the bottom of the bus, it might be a wheel
                    if (Math.abs(relativeY) < 1.0 &&
                        child.geometry.boundingSphere &&
                        child.geometry.boundingSphere.radius < this.width / 2) {
                        //.log(`Found potential wheel by position: ${child.name}`);
                        isWheel = true;
                    }
                }

                if (isWheel) {
                    this.wheels.push(child);
                }
            }
        });

        //console.log(`Found ${this.wheels.length} wheels in the model`);

        // If no wheels found, we need to create them manually
        if (this.wheels.length === 0) {
            //console.log("No wheels found in model, creating manually");
            this.createWheels();
        } else if (this.wheels.length < 4) {
            // If we found some wheels but not enough, find the missing ones by symmetry
            //console.log("Not enough wheels found, attempting to find symmetrical pairs");
            this.completeWheelSet();
        }
    }

    completeWheelSet() {
        // Try to complete the wheel set if we have some wheels but not all four
        // This assumes wheels come in symmetrical pairs

        const originalCount = this.wheels.length;

        // Group wheels by y-position (assuming wheels at same height are on same axle)
        const wheelsByHeight = {};
        for (const wheel of this.wheels) {
            const position = new THREE.Vector3();
            wheel.getWorldPosition(position);
            const heightKey = position.y.toFixed(2);

            if (!wheelsByHeight[heightKey]) {
                wheelsByHeight[heightKey] = [];
            }
            wheelsByHeight[heightKey].push(wheel);
        }

        // For each height that has a single wheel, try to find or create its pair
        for (const heightKey in wheelsByHeight) {
            const wheelsAtHeight = wheelsByHeight[heightKey];
            if (wheelsAtHeight.length === 1) {
                const wheel = wheelsAtHeight[0];
                const position = new THREE.Vector3();
                wheel.getWorldPosition(position);

                // Create a mirrored wheel on the opposite side
                //console.log(`Creating symmetrical pair for wheel at height ${heightKey}`);

                // Clone the existing wheel
                const mirroredWheel = wheel.clone();

                // Mirror the x position (assuming x is the side-to-side axis)
                mirroredWheel.position.x = -wheel.position.x;

                // Add to the model and wheel array
                this.model.add(mirroredWheel);
                this.wheels.push(mirroredWheel);
            }
        }

        //console.log(`Added ${this.wheels.length - originalCount} wheels to complete the set`);
        //console.log(`Final wheel count: ${this.wheels.length}`);

        // If we still don't have enough wheels, create them manually
        if (this.wheels.length < 4) {
            //console.log("Still not enough wheels, creating the rest manually");
            this.createWheels();
        }
    }

    createSimpleBus() {
        // Just delegate to our custom bus creation method
        this.createCustomBus();
    }

    addBusWindows() {
        // Bus dimensions reference
        const bodyWidth = this.width;
        const bodyHeight = this.height;
        const bodyLength = this.length;

        // Window material - simplified for temporary model
        const windowMaterial = new THREE.MeshStandardMaterial({
            color: 0x88CCFF,
            transparent: true,
            opacity: 0.5,
            metalness: 0.1,
            roughness: 0.2
        });

        // Front windshield - simplified
        const frontWindow = new THREE.Mesh(
            new THREE.BoxGeometry(bodyWidth * 0.8, bodyHeight * 0.35, 0.1),
            windowMaterial
        );
        frontWindow.position.set(0, bodyHeight * 0.1, bodyLength * 0.5 - 0.05);
        this.mesh.add(frontWindow);

        // Rear windshield - simplified
        const rearWindow = new THREE.Mesh(
            new THREE.BoxGeometry(bodyWidth * 0.8, bodyHeight * 0.35, 0.1),
            windowMaterial
        );
        rearWindow.position.set(0, bodyHeight * 0.1, -bodyLength * 0.5 + 0.05);
        this.mesh.add(rearWindow);

        // Side windows - simplified to 3 large segments per side
        const windowSegments = 3;
        const windowLength = bodyLength * 0.25;
        const spacing = bodyLength * 0.05;

        // Create simplified window segments
        for (let i = 0; i < windowSegments; i++) {
            const zPos = (bodyLength * 0.3) - (i * (windowLength + spacing));

            // Left side window
            const leftWindow = new THREE.Mesh(
                new THREE.BoxGeometry(0.1, bodyHeight * 0.35, windowLength),
                windowMaterial
            );
            leftWindow.position.set(-bodyWidth * 0.5 - 0.05, bodyHeight * 0.1, zPos);
            this.mesh.add(leftWindow);

            // Right side window
            const rightWindow = new THREE.Mesh(
                new THREE.BoxGeometry(0.1, bodyHeight * 0.35, windowLength),
                windowMaterial
            );
            rightWindow.position.set(bodyWidth * 0.5 + 0.05, bodyHeight * 0.1, zPos);
            this.mesh.add(rightWindow);
        }
    }

    createWheels() {
        // Create wheels for the bus
        const wheelGeometry = new THREE.CylinderGeometry(
            this.wheelRadius, this.wheelRadius, this.wheelWidth, 16
        );
        const wheelMaterial = new THREE.MeshStandardMaterial({
            color: 0x222222,
            roughness: 0.7,
            metalness: 0.1
        });

        // Rotate wheel geometry to align with the axle - now X is front to back
        wheelGeometry.rotateX(Math.PI / 2);

        // Create 4 wheels
        this.wheels = [];

        // Wheel positions - adjusted for a realistic school bus
        // Longer distance between front and back wheels
        const axleOffset = this.length * 0.35; // Distance from center to axles
        const frontZ = axleOffset * 0.5;      // Front axle is closer to center
        const backZ = -axleOffset * 0.9;      // Back axle is further back (for better weight distribution)
        const sideOffset = this.width * 0.55; // Wheels slightly outside the body
        const wheelY = -this.height * 0.5 + this.wheelRadius * 0.8; // Lower position for wheels

        // Positions array [front-left, front-right, back-left, back-right]
        const wheelPositions = [
            // Front wheels
            new THREE.Vector3(-sideOffset, wheelY, frontZ),  // Front left
            new THREE.Vector3(sideOffset, wheelY, frontZ),   // Front right
            // Back wheels
            new THREE.Vector3(-sideOffset, wheelY, backZ),   // Back left
            new THREE.Vector3(sideOffset, wheelY, backZ)     // Back right
        ];

        // Create and add each wheel
        for (let i = 0; i < wheelPositions.length; i++) {
            const wheel = new THREE.Mesh(wheelGeometry, wheelMaterial);
            wheel.position.copy(wheelPositions[i]);
            wheel.castShadow = true;
            wheel.receiveShadow = true;

            // Name wheels to make them easier to identify
            wheel.name = i < 2 ? `wheel_front_${i === 0 ? 'left' : 'right'}` : `wheel_back_${i === 2 ? 'left' : 'right'}`;

            // Add to the mesh and store reference
            this.mesh.add(wheel);
            this.wheels.push(wheel);

            //console.log(`Created wheel: ${wheel.name} at position ${wheel.position.x.toFixed(2)}, ${wheel.position.y.toFixed(2)}, ${wheel.position.z.toFixed(2)}`);
        }

        // Log wheels count
        //console.log(`Created ${this.wheels.length} wheels manually`);
    }

    createDebugDisplay() {
        // Create a simple div for displaying speed
        this.speedDisplay = document.createElement('div');
        this.speedDisplay.style.position = 'absolute';
        this.speedDisplay.style.bottom = '20px';
        this.speedDisplay.style.left = '20px';
        this.speedDisplay.style.padding = '10px';
        this.speedDisplay.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
        this.speedDisplay.style.color = 'white';
        this.speedDisplay.style.fontFamily = 'monospace';
        this.speedDisplay.style.fontSize = '16px';
        this.speedDisplay.style.borderRadius = '5px';
        this.speedDisplay.innerHTML = 'Speed: 0 km/h';
        this.speedDisplay.style.display = 'none'; // Hide this as we'll use the Game's speed display
        document.body.appendChild(this.speedDisplay);

        // Create a div for the debug info
        this.debugInfo = document.createElement('div');
        this.debugInfo.style.position = 'absolute';
        this.debugInfo.style.top = '20px';
        this.debugInfo.style.left = '20px';
        this.debugInfo.style.padding = '10px';
        this.debugInfo.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
        this.debugInfo.style.color = 'white';
        this.debugInfo.style.fontFamily = 'monospace';
        this.debugInfo.style.fontSize = '14px';
        this.debugInfo.style.borderRadius = '5px';
        this.debugInfo.style.display = 'none'; // Hidden by default
        this.debugInfo.style.whiteSpace = 'pre';
        this.debugInfo.style.maxHeight = '80vh';
        this.debugInfo.style.overflow = 'auto';
        this.debugInfo.innerHTML = 'Debug Info';
        document.body.appendChild(this.debugInfo);
    }

    initParticleSystem() {
        const geometry = new BufferGeometry();
        geometry.setAttribute('position', new Float32BufferAttribute(this.particles, 3));

        // Add size attribute for dynamic sizing
        const sizes = new Float32Array(this.particleCount);
        for (let i = 0; i < this.particleCount; i++) {
            sizes[i] = 0.1; // Default size
        }
        geometry.setAttribute('size', new Float32BufferAttribute(sizes, 1));

        const material = new PointsMaterial({
            color: 0x666666,
            transparent: true,
            opacity: 0.6,
            sizeAttenuation: true,
            vertexColors: false
        });

        this.particleSystem = new Points(geometry, material);
        this.scene.add(this.particleSystem);

        // Initialize particle lifetimes
        for (let i = 0; i < this.particleCount; i++) {
            this.particleLifetimes[i] = 0;
        }
    }

    updateParticles(deltaTime) {
        if (!this.particleSystem) {
            this.initParticleSystem();
            return;
        }

        const positions = this.particleSystem.geometry.attributes.position.array;
        const speed = Math.abs(this.getSpeed());
        const isMoving = speed > 0.1 && this.throttle > 0;

        // Update existing particles
        for (let i = 0; i < this.particleCount; i++) {
            const i3 = i * 3;

            if (this.particleLifetimes[i] > 0) {
                // Update position based on velocity
                positions[i3] += this.particleVelocities[i3] * deltaTime;
                positions[i3 + 1] += this.particleVelocities[i3 + 1] * deltaTime;
                positions[i3 + 2] += this.particleVelocities[i3 + 2] * deltaTime;

                // Update lifetime
                this.particleLifetimes[i] -= deltaTime;

                // Calculate dynamic size based on speed and throttle
                const baseSize = 0.1;
                const speedFactor = Math.min(speed / 50, 1.0); // Normalize speed
                const throttleFactor = Math.max(0.2, this.throttle);
                const lifetimeFactor = this.particleLifetimes[i] / 2.0;
                const dynamicSize = baseSize * (1 + speedFactor * 2) * throttleFactor;

                // Update size attribute
                this.particleSystem.geometry.attributes.size.array[i] = dynamicSize;

                // Fade out particles
                const opacity = lifetimeFactor;
                this.particleSystem.material.opacity = Math.min(0.6, opacity);
            } else if (isMoving && Math.random() < 0.1) {
                // Emit new particle from exhaust position
                const exhaustWorldPos = this.exhaustPosition.clone().applyMatrix4(this.mesh.matrixWorld);
                positions[i3] = exhaustWorldPos.x;
                positions[i3 + 1] = exhaustWorldPos.y;
                positions[i3 + 2] = exhaustWorldPos.z;

                // Set velocity with some randomness
                this.particleVelocities[i3] = (Math.random() - 0.5) * speed * 0.1;
                this.particleVelocities[i3 + 1] = Math.random() * speed * 0.2;
                this.particleVelocities[i3 + 2] = Math.random() * speed * 0.1;

                // Set lifetime between 1-2 seconds
                this.particleLifetimes[i] = 1 + Math.random();
            }
        }

        this.particleSystem.geometry.attributes.position.needsUpdate = true;
        this.particleSystem.geometry.attributes.size.needsUpdate = true;
    }

    update(deltaTime) {
        // Skip update if physics body doesn't exist yet
        if (!this.physicsBody) return;

        // Handle user input
        this.handleInput(deltaTime);

        // Apply physics forces
        this.applyForces(deltaTime);

        // Update suspension and wheel physics
        this.updateSuspension(deltaTime);

        // Check if bus is on ground
        this.checkGrounded();

        // Update fuel consumption
        this.updateFuel(deltaTime);

        // Update visual position to match physics body
        this.updateVisualPosition();

        // Update particle system
        this.updateParticles(deltaTime);

        // Update engine sound based on speed and acceleration
        if (window.game && window.game.audioManager) {
            const speed = this.getSpeed();
            window.game.audioManager.updateEngineSound(speed, this.throttle);

            // Start engine idle sound when engine starts
            if (this.engineStarted && !window.game.audioManager.sounds['engine_idle'].playing) {
                window.game.audioManager.playEngineIdle();
            }
        }

        // Detect if we're on a road
        if (!this.freeDriveMode) {
            this.detectCurrentRoad();
        }

        // Update debug display
        this.updateDebugDisplay();
    }

    handleInput(deltaTime) {
        // Instead of checking individual keys, use the InputController's helper methods
        const forwardInput = this.inputController.getForwardInput();
        const rightInput = this.inputController.getRightInput();

        // Map input values to actions
        const forward = forwardInput > 0;
        const backward = forwardInput < 0;
        const left = rightInput < 0;
        const right = rightInput > 0;

        // Use S key for braking
        const brakeInput = typeof this.inputController.getBrakeInput === 'function'
            ? this.inputController.getBrakeInput()
            : (this.inputController.isKeyDown('s') || this.inputController.isKeyDown('S') ? 1 : 0);
        const brake = brakeInput > 0.1;
        const brakeIncrement = 2.0 * deltaTime;
        const brakeDecrement = 3.0 * deltaTime;

        // Handle horn (H key)
        const hornPressed = typeof this.inputController.getHornInput === 'function'
            ? this.inputController.getHornInput()
            : (this.inputController.isKeyDown('h') || this.inputController.isKeyDown('H'));
        if (hornPressed && !this.lastHornPressed && window.game && window.game.audioManager) {
            window.game.audioManager.playHorn();
        }
        this.lastHornPressed = hornPressed;

        // Debug toggle (T key)
        const tPressed = this.inputController.isKeyDown('t') || this.inputController.isKeyDown('T');
        if (tPressed && !this.lastTPressed) {
            this.debug = !this.debug;
            this.debugInfo.style.display = this.debug ? 'block' : 'none';
            //console.log(`Debug display ${this.debug ? 'enabled' : 'disabled'}`);
        }
        this.lastTPressed = tPressed;

        // Track if there's any driving input (throttle or brake)
        const hasInput = forward || backward || brake;

        // Engine management - start engine with input, stop after no input for a while
        if (hasInput) {
            this.engineStarted = true;
            this.lastInputTime = performance.now();
        } else if (this.engineStarted && this.lastInputTime) {
            // Turn off engine after 30 seconds of no input
            const idleTime = performance.now() - this.lastInputTime;
            if (idleTime > 30000) { // 30 seconds
                this.engineStarted = false;
            }
        }

        // Set acceleration target (-1 to 1)
        if (forward && !backward) {
            this.targetThrottle = 1;
            this.targetBrake = 0;
        } else if (backward && !forward) {
            // For reversing, we need to use brake, not negative throttle
            this.targetThrottle = 0;
            this.targetBrake = 1; // Use the brake for reverse
        } else {
            this.targetThrottle = 0;

            // Apply gentle engine braking when coasting
            const speed = this.getSpeed();
            if (Math.abs(speed) > 0.5) {
                this.targetBrake = 0.2; // Light engine braking, not heavy auto-brake
            } else {
                this.targetBrake = 0;
            }
        }


        if (brake) {
            this.targetBrake = Math.min(this.targetBrake + brakeIncrement * 2, 1); // Faster brake application
            this.targetThrottle = 0; // Cut throttle completely when braking
        
            if (window.game && window.game.audioManager) {
                window.game.audioManager.playBraking();
                // Ensure engine sound transitions to idle when braking
                const speed = this.getSpeed();
                window.game.audioManager.updateEngineSound(speed, 0);
            }
        } else {
            this.targetBrake = Math.max(this.targetBrake - brakeDecrement, 0);
        
            if (window.game && window.game.audioManager) {
                window.game.audioManager.stopBraking();
            }
        }

        // Apply dedicated brake if space is pressed
        /** 
        if (brake) {
            this.targetBrake = 1;

            // Reduce throttle when braking
            this.targetThrottle *= 0.5;

            // Play braking sound
            if (window.game && window.game.audioManager) {
                window.game.audioManager.playBraking();
            }
        } else {
            // Stop braking sound when brake is released
            if (window.game && window.game.audioManager) {
                window.game.audioManager.stopBraking();
            }
        }
            */

        // Set steering target based on input
        const steeringTarget = rightInput;

        // Apply steering rate limit — fast enough to feel responsive but still smooth
        const steeringRate = Math.min(1, 8.0 * deltaTime); // ~8x faster than before
        this.steering = this.steering + (steeringTarget - this.steering) * steeringRate;

        // Apply smooth acceleration
        this.throttle = this.smoothValue(
            this.throttle,
            this.targetThrottle,
            this.accelerationRate,
            deltaTime
        );

        // Apply smooth braking
        this.brake = this.smoothValue(
            this.brake,
            this.targetBrake,
            this.brakeRate,
            deltaTime
        );
    }

    applyForces(deltaTime) {
        // Skip if physics body is not available
        if (!this.physicsBody) return;

        // Check if the vehicle is grounded
        const isGrounded = this.checkGrounded();

        // Get current velocity
        const velocity = this.getLinearVelocity();
        this.velocity.copy(velocity);

        // Calculate current speed in m/s and km/h
        const speed = this.getSpeed();
        const speedKmh = speed * 3.6; // Convert m/s to km/h

        // Direction-aware forces
        const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(this.getFacingQuaternion());

        // Get terrain information
        this.detectCurrentRoad();

        // Apply common forces like drag and downforce
        this.applyCommonForces(velocity, speed);

        // Apply standard driving mechanics
        this.applyStandardDrivingForces(deltaTime, forward, velocity, speed, speedKmh);
    }

    updateDebugDisplay() {
        if (!this.debugInfo) return;

        // Get current velocity
        const velocity = this.getLinearVelocity();
        const speed = this.getSpeed(); // m/s
        const speedKmh = speed * 3.6;  // km/h

        // Get position
        const position = this.getPosition();

        // Get angular velocity
        const angVel = this.getAngularVelocity();

        // Get gas station info if available from the game
        let gasStationInfo = "";
        if (window.game && window.game.gasStations) {
            // Find closest gas station
            let closestStation = null;
            let closestDistance = Infinity;

            for (const station of window.game.gasStations) {
                const stationPos = new THREE.Vector3(station.position.x, 0, station.position.z);
                const distance = position.distanceTo(stationPos);

                if (distance < closestDistance) {
                    closestDistance = distance;
                    closestStation = station;
                }
            }

            // Add gas station info if one was found
            if (closestStation) {
                const inRange = closestDistance < closestStation.refuelDistance;
                gasStationInfo = `
                    <div class="debug-section">
                        <h3>Gas Station Info</h3>
                        <div>Nearest: ${closestStation.name}</div>
                        <div>Distance: ${closestDistance.toFixed(1)} units</div>
                        <div style="color: ${inRange ? '#00ff00' : '#ff0000'}">
                            Status: ${inRange ? 'IN RANGE - PRESS G TO REFUEL' : 'NOT IN RANGE'}
                        </div>
                    </div>
                `;
            }
        }

        // Format physics debug info
        const debugText = `
            <div class="debug-section">
                <h3>Bus Debug Info</h3>
                <div>Speed: ${speedKmh.toFixed(1)} km/h (${speed.toFixed(2)} m/s)</div>
                <div>Position: (${position.x.toFixed(1)}, ${position.y.toFixed(1)}, ${position.z.toFixed(1)})</div>
                <div>Velocity: (${velocity.x.toFixed(2)}, ${velocity.y.toFixed(2)}, ${velocity.z.toFixed(2)})</div>
                <div>Angular Velocity: (${angVel.x.toFixed(2)}, ${angVel.y.toFixed(2)}, ${angVel.z.toFixed(2)})</div>
                <div>Input: Throttle=${this.throttle.toFixed(2)}, Brake=${this.brake.toFixed(2)}, Steering=${this.steering.toFixed(2)}</div>
                <div>Grounded: ${this.wheelsOnGround.filter(w => w).length}/4 wheels</div>
                <div>Current Road: ${this.currentRoad ? this.currentRoad.name : 'off-road'}</div>
                <div>Fuel: ${this.fuel.toFixed(1)}/${this.fuelCapacity} (${(this.fuel / this.fuelCapacity * 100).toFixed(1)}%)</div>
                <div>Engine: ${this.engineStarted ? 'Running' : 'Off'}</div>
            </div>
            
            ${gasStationInfo}
            
            <div class="debug-section">
                <h3>Controls</h3>
                <div>WASD/Arrows: Drive</div>
                <div>Space: Brake</div>
                <div>G: Refuel (at gas station)</div>
                <div>T: Toggle Debug</div>
                <div>V: Toggle Camera View</div>
                <div>H: Horn</div>
            </div>
        `;

        // Update debug info
        this.debugInfo.innerHTML = debugText;
    }

    // Update fuel consumption based on driving behavior
    updateFuel(deltaTime) {
        if (!this.physicsBody) return;

        const now = performance.now();

        // Only update fuel every 100ms for performance
        if (now - this.fuelLastUpdate < 100) return;

        // Convert time delta to minutes for fuel calculation
        const deltaMinutes = (now - this.fuelLastUpdate) / 60000;
        this.fuelLastUpdate = now;

        // Get current speed in km/h
        const speedKmh = this.getSpeed() * 3.6;

        // Calculate fuel consumption
        let fuelUsed = 0;

        // Check if engine is active (either through throttle input or idle state)
        const engineRunning = Math.abs(this.throttle) > 0.01 || this.engineStarted;

        if (engineRunning) {
            if (Math.abs(speedKmh) < 1) {
                // Idle fuel consumption - only if engine is running
                fuelUsed = this.fuelConsumptionRate.idle * deltaMinutes;
            } else {
                // Driving fuel consumption based on speed and throttle
                const throttleFactor = Math.abs(this.throttle);

                // Base consumption
                const baseConsumption = this.fuelConsumptionRate.driving * throttleFactor * deltaMinutes;

                // Speed-based consumption with exponential increase at higher speeds
                let speedFactor = this.fuelConsumptionRate.speedFactor;
                if (Math.abs(speedKmh) > 60) {
                    // Exponentially increase consumption at higher speeds
                    speedFactor = this.fuelConsumptionRate.speedFactor * (1 + (Math.abs(speedKmh) - 60) / 20);
                }
                const speedConsumption = Math.abs(speedKmh) * speedFactor * deltaMinutes;

                // Add acceleration penalty - if throttle is high, consume more fuel
                let accelerationConsumption = 0;
                if (throttleFactor > 0.7) {
                    accelerationConsumption = throttleFactor * 3.0 * deltaMinutes;
                }

                // Total consumption
                fuelUsed = baseConsumption + speedConsumption + accelerationConsumption;
            }
        }
        // If engine is off, no fuel is consumed

        // Apply fuel consumption
        this.fuel = Math.max(0, this.fuel - fuelUsed);

        // If out of fuel, disable engine power
        if (this.fuel <= 0) {
            // Allow coasting to a stop but no new acceleration
            this.enginePower = 0;
            this.reverseEnginePower = 0;
        } else if (this.enginePower === 0) {
            // Fuel was restored (e.g. by a gas station) — re-enable engine
            this.enginePower = 300;
            this.reverseEnginePower = 80;
            this.inputController.isGameOver = false;
        }

        // Emit custom event so Game class can update UI
        if (window.game) {
            const fuelEvent = new CustomEvent('fuel_updated', {
                detail: {
                    level: this.fuel,
                    capacity: this.fuelCapacity,
                    percentage: this.fuel / this.fuelCapacity * 100,
                    empty: this.fuel <= 0
                }
            });
            window.dispatchEvent(fuelEvent);

            // Set game over state when fuel runs out
            if (this.fuel <= 0) {
                this.inputController.isGameOver = true;
                // Stop all sounds when out of fuel
                if (window.game && window.game.audioManager) {
                    window.game.audioManager.stopAllSounds();
                }
            }
        }
    }

    // Helper functions to interact with physics
    addForce(force) {
        if (this.physicsBody) {
            this.physicsBody.addForce({
                x: force.x,
                y: force.y,
                z: force.z
            });
        }
    }

    addTorque(torque) {
        if (this.physicsBody) {
            this.physicsBody.addTorque({
                x: torque.x,
                y: torque.y,
                z: torque.z
            });
        }
    }

    // Handle collecting a coin when physics detects overlap
    collectCoin(coin) {
        if (!coin || coin.collected) return;

        //console.log("Collecting coin:", coin.value, "Current coins:", this.coins);

        // Increment coin count
        this.coins += coin.value;

        //console.log("New coin count:", this.coins);

        // Mark coin as collected and trigger its collection animation
        coin.collect();

        // Emit event for UI update
        const coinEvent = new CustomEvent('coins_updated', {
            detail: {
                coins: this.coins,
                coin: coin
            }
        });
        window.dispatchEvent(coinEvent);
        //console.log("Dispatched coins_updated event with total:", this.coins);
    }

    getLinearVelocity() {
        if (this.physicsBody) {
            const vel = this.physicsBody.getLinearVelocity();
            return new THREE.Vector3(vel.x, vel.y, vel.z);
        }
        return new THREE.Vector3();
    }

    setLinearVelocity(velocity) {
        if (this.physicsBody && !this.physics.usingFallbackPhysics && typeof this.physicsBody.setLinearVelocity === 'function') {
            this.physicsBody.setLinearVelocity({
                x: velocity.x,
                y: velocity.y,
                z: velocity.z
            });
        } else if (this.physicsBody && this.physicsBody.velocity) {
            this.physicsBody.velocity.set(velocity.x, velocity.y, velocity.z);
        } else if (this.physics.usingFallbackPhysics && this.physicsBody.threeMesh) {
            this.velocity.copy(velocity);
        }
    }

    getFacingQuaternion() {
        return this.mesh ? this.mesh.quaternion.clone() : new THREE.Quaternion();
    }

    getPosition() {
        return this.mesh ? this.mesh.position.clone() : new THREE.Vector3();
    }

    getSpeed() {
        if (!this.physicsBody) return 0;

        try {
            // Get the current velocity vector
            const velocity = this.getLinearVelocity();

            // Calculate speed magnitude (in m/s)
            let speedInMetersPerSecond = velocity.length();

            // Get direction to determine if moving forward or backward
            if (this.mesh) {
                const busRotation = this.getFacingQuaternion();
                const forwardDir = new THREE.Vector3(0, 0, -1).applyQuaternion(busRotation);
                const velocityDir = velocity.clone().normalize();
                const dotProduct = forwardDir.dot(velocityDir);

                // If dot product is negative, we're moving backward
                if (dotProduct < 0 && speedInMetersPerSecond > 0.1) {
                    // Return negative speed for backward movement
                    return -speedInMetersPerSecond;
                }
            }
            //sconsole.log(speedInMetersPerSecond)
            return speedInMetersPerSecond;
        } catch (error) {
            //console.warn("Error getting bus speed:", error);
            return 0;
        }
    }

    getAngularVelocity() {
        if (this.physicsBody && typeof this.physicsBody.getAngularVelocity === 'function') {
            try {
                const angVel = this.physicsBody.getAngularVelocity();
                // Ensure we return a valid THREE.Vector3
                return new THREE.Vector3(
                    angVel && angVel.x !== undefined ? angVel.x : 0,
                    angVel && angVel.y !== undefined ? angVel.y : 0,
                    angVel && angVel.z !== undefined ? angVel.z : 0
                );
            } catch (error) {
                //console.warn("Error getting angular velocity:", error);
                return new THREE.Vector3();
            }
        }
        return new THREE.Vector3();
    }

    applyDrag() {
        // Apply drag/air resistance
        if (this.physicsBody) {
            // Linear drag - air resistance
            const currentVelocity = this.getLinearVelocity();
            const speed = currentVelocity.length();

            // Drag increases with speed (quadratic drag model)
            if (speed > 0.01) {
                // Drag coefficient and frontal area
                const airDensity = 1.225; // kg/m^3
                const dragCoefficient = 0.7; // Typical for buses
                const frontalArea = this.width * this.height; // m^2

                // Drag formula: Fd = 0.5 * p * v^2 * Cd * A
                // Where:
                // p = air density
                // v = velocity
                // Cd = drag coefficient
                // A = frontal area
                const dragFactor = 0.5 * airDensity * dragCoefficient * frontalArea / this.mass;

                // Compute drag force (acts in opposite direction of motion)
                // Simplified for gameplay
                const dragForce = currentVelocity.clone().negate().multiplyScalar(
                    dragFactor * currentVelocity.lengthSq()
                );
                this.addForce(dragForce);
            }

            // Apply angular drag to prevent excessive spinning
            try {
                // Get the angular velocity and apply drag if it's provided by the physics engine
                if (typeof this.physicsBody.getAngularVelocity === 'function') {
                    const angularVelocity = this.getAngularVelocity();
                    // Convert to THREE.Vector3 to ensure lengthSq is available
                    const angVelVector = new THREE.Vector3(
                        angularVelocity.x || 0,
                        angularVelocity.y || 0,
                        angularVelocity.z || 0
                    );

                    if (angVelVector.lengthSq() > 0.01) {
                        const angularDrag = angVelVector.clone().negate().multiplyScalar(0.2);
                        this.addTorque(angularDrag);
                    }
                } else {
                    // Fallback for when getAngularVelocity is not available
                    // Apply a simple upward correction if spinning
                    if (this.mesh && Math.abs(this.mesh.rotation.z) > 0.1) {
                        // Apply a corrective torque to level the bus
                        const correction = new THREE.Vector3(0, 0, -this.mesh.rotation.z * 10);
                        this.addTorque(correction);
                    }
                }
            } catch (error) {
                //console.warn("Angular drag application error:", error.message);
                // No need for another fallback as we already have one above
            }
        }
    }

    initPhysics() {
        // Create physics body for bus
        this.physics = new SimplePhysics({
            position: new THREE.Vector3(0, 0, 0),
            mass: 8500, // Bus mass in kg
            drag: 0.2,  // Air resistance
            angularDrag: 1.2, // Increased angular drag for better stability
            useGravity: false, // We'll handle gravity manually
            bounciness: 0.2, // Increased bounciness to better handle terrain obstacles
            linearDamping: 0.05, // Damping to simulate suspension
            angularDamping: 0.4  // Increased angular damping
        });

        // Set initial physics position to match model
        if (this.mesh) {
            this.physics.setPosition(this.mesh.position);
            this.physics.setRotation(this.mesh.quaternion);
        }

        // Initialize wheels to not be on ground initially
        this.wheelsOnGround = [false, false, false, false];
    }

    updateSuspension(deltaTime) {
        if (!this.mesh) return;

        // For a more sophisticated suspension system in free driving
        const currentPosition = this.mesh.position.clone();
        const currentVelocity = this.getLinearVelocity();
        const isGrounded = this.checkGrounded();

        // Only apply suspension when grounded
        if (!isGrounded) return;

        // Keep suspension forces purely vertical (world up)
        // Terrain slope alignment is handled visually by alignToTerrainSlope()
        const upVector = new THREE.Vector3(0, 1, 0);

        // Get terrain height for suspension compression calculation
        const heightMap = this.scene?.userData?.terrainHeightMap;
        const terrainY = heightMap
            ? heightMap.getHeight(currentPosition.x, currentPosition.z)
            : 0;

        // Suspension properties - softer for off-road
        const suspensionStrength = 35000; // Spring force
        const suspensionDamping = 4500;   // Damping force
        const restHeight = 2.5;           // Ground clearance above terrain
        const maxExtension = 0.8;         // How much suspension can extend/compress

        // Calculate compression relative to terrain surface
        const heightAboveTerrain = currentPosition.y - terrainY;
        const compression = Math.max(0, Math.min(heightAboveTerrain - restHeight, maxExtension));
        const compressionRate = compression / maxExtension;

        // Calculate spring force vertically (safe — won't launch bus sideways)
        let suspensionForce = upVector.clone().multiplyScalar(
            suspensionStrength * compressionRate * deltaTime
        );

        // Add damping based on vertical velocity only
        const verticalVelocity = currentVelocity.y;
        suspensionForce.add(
            upVector.clone().multiplyScalar(
                -verticalVelocity * suspensionDamping * deltaTime
            )
        );

        // Apply suspension force
        this.addForce(suspensionForce);

        // Anti-roll force — keep targeting world up for physics stability
        // The visual slope alignment is handled separately by alignToTerrainSlope()
        const right = new THREE.Vector3(1, 0, 0).applyQuaternion(this.mesh.quaternion);
        const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(this.mesh.quaternion);

        // Calculate tilt angles relative to world up
        const rightDot = right.dot(upVector);
        const forwardDot = forward.dot(upVector);

        // Keep original anti-roll strength for physics stability
        const antiRollStrength = 12000 * deltaTime;

        // Side-to-side anti-roll
        if (Math.abs(rightDot) > 0.05) {
            const sideAntiRoll = new THREE.Vector3().copy(right)
                .multiplyScalar(-rightDot * antiRollStrength);
            sideAntiRoll.y = Math.abs(sideAntiRoll.y);
            this.addTorque(sideAntiRoll);
        }

        // Front-to-back anti-roll
        if (Math.abs(forwardDot) > 0.05) {
            const forwardAntiRoll = new THREE.Vector3().copy(forward)
                .multiplyScalar(-forwardDot * antiRollStrength);
            forwardAntiRoll.y = Math.abs(forwardAntiRoll.y);
            this.addTorque(forwardAntiRoll);
        }
    }

    // Detect which road the bus is currently on and update speed limits
    detectCurrentRoad() {
        if (!this.mesh) return;

        // Bus position (to check against roads)
        const busPosition = new THREE.Vector3();
        this.mesh.getWorldPosition(busPosition);

        // Get reference to the roads array from the game
        const roads = this.scene.userData.game?.roads || [];

        // If there are no roads, use off-road speed limits
        if (!roads || roads.length === 0) {
            this.currentRoad = null;
            this.currentSpeedLimits = { forward: 300, backward: 200, corner: 150 }; // Increased to match higher speeds
            if (this.debug) {
                //console.log('No roads available, using terrain speed limits');
            }
            return;
        }

        // Find closest road
        let closestRoad = null;
        let closestDistance = Infinity;

        for (const roadObject of roads) {
            const road = roadObject.road || roadObject;
            if (!road) continue;

            // Get road bounds
            let roadPosition, roadSize;

            if (road.position) {
                roadPosition = road.position.clone();
                roadSize = new THREE.Vector2(road.width || 10, road.length || 200);
            } else if (roadObject.position) {
                roadPosition = roadObject.position.clone();
                roadSize = new THREE.Vector2(
                    roadObject.userData?.roadWidth || 10,
                    roadObject.userData?.roadLength || 200
                );
            } else {
                continue;
            }

            // Calculate distance to road center
            const distance = busPosition.distanceTo(roadPosition);

            // Check if this is the closest road
            if (distance < closestDistance) {
                closestDistance = distance;
                closestRoad = road;
            }
        }

        // Check if we're on a road (within some margin of its width)
        const onRoadThreshold = 10; // Distance in units to consider "on road"

        if (closestDistance < onRoadThreshold && closestRoad) {
            this.currentRoad = closestRoad;

            // Update speed limits if the road has them
            if (closestRoad.speedLimits) {
                this.currentSpeedLimits = closestRoad.speedLimits;
            } else if (closestRoad.getSpeedLimitData) {
                this.currentSpeedLimits = closestRoad.getSpeedLimitData();
            }
            // Otherwise use defaults based on road type
            else if (closestRoad.type) {
                switch (closestRoad.type) {
                    case 'urban':
                        this.currentSpeedLimits = { forward: 200, backward: 150, corner: 100 };
                        break;
                    case 'highway':
                        this.currentSpeedLimits = { forward: 300, backward: 200, corner: 150 };
                        break;
                    default:
                        this.currentSpeedLimits = { forward: 250, backward: 180, corner: 120 };
                }
            }

            if (this.debug) {
                //console.log(`On road: ${closestRoad.type || 'standard'}, Speed limits: ${JSON.stringify(this.currentSpeedLimits)}`);
            }
        } else {
            // Not on a road, use off-road/terrain speed limits
            this.currentRoad = null;
            this.currentSpeedLimits = { forward: 300, backward: 200, corner: 150 }; // Increased to match higher speeds

            if (this.debug) {
                //console.log('Off-road, using terrain speed limits');
            }
        }
    }

    checkGrounded() {
        // In a real simulation, we'd do raycasts from each wheel
        // For free-driving mode, we'll consider the bus grounded anywhere on the terrain
        // We'll use a more sophisticated method that accounts for the bus orientation

        if (!this.mesh) return false;

        // Get terrain height at bus position
        const heightMap = this.scene?.userData?.terrainHeightMap;
        const terrainY = heightMap
            ? heightMap.getHeight(this.mesh.position.x, this.mesh.position.z)
            : 0;
        const heightAboveTerrain = this.mesh.position.y - terrainY;

        // For true off-road experience, we'll allow more height variation
        const maxGroundedHeight = 10; // Increased height threshold

        // Basic height check (relative to terrain)
        if (heightAboveTerrain > maxGroundedHeight) {
            return false;
        }

        // Check if we're moving upward too quickly (jumping)
        const velocity = this.getLinearVelocity();
        if (velocity.y > 12) {
            return false;
        }

        // We're grounded if we're close enough to the terrain surface
        const groundProximity = heightAboveTerrain < 4;

        // Additional check for steep terrain
        // If we're on steep terrain but moving, we should still be considered grounded
        const speed = new THREE.Vector3(velocity.x, 0, velocity.z).length();
        const movingOnSteepTerrain = speed > 2 && heightAboveTerrain < 8;

        return groundProximity || movingOnSteepTerrain;
    }

    updateVisualPosition() {
        if (!this.physicsBody || !this.mesh) return;

        if (this.physics.usingFallbackPhysics) {
            // Mesh is already updated by the physics system in fallback mode
            // But we still need to apply terrain slope alignment
            this.alignToTerrainSlope();
            return;
        }

        // Get current transform from physics
        const pose = this.physicsBody.getGlobalPose();

        // Update the mesh position based on the physics body position
        const physicsPos = new THREE.Vector3(pose.p.x, pose.p.y, pose.p.z);

        // Store the offset from center of mass to bottom of bus
        // This calculation ensures wheels touch the ground
        if (!this.modelOffset) {
            // Calculate only once when model is available
            const boundingBox = new THREE.Box3().setFromObject(this.mesh);
            this.modelOffset = new THREE.Vector3(0, -boundingBox.min.y + 0.05, 0);
        }

        // Apply offset to position the model correctly relative to physics body
        if (this.modelOffset) {
            // Clone physics position and add the offset
            const adjustedPos = physicsPos.clone();

            // Get terrain height at bus position for proper ground clamping
            const heightMap = this.scene?.userData?.terrainHeightMap;
            const terrainY = heightMap
                ? heightMap.getHeight(adjustedPos.x, adjustedPos.z)
                : 0;

            // For the y-position, use physics position but ensure the bottom is at ground level
            adjustedPos.y = Math.max(terrainY + 0.05, adjustedPos.y - this.height / 2 + this.modelOffset.y);

            // Update the mesh position
            this.mesh.position.copy(adjustedPos);
        } else {
            // Fallback if offset isn't calculated yet
            this.mesh.position.copy(physicsPos);
        }

        // Update rotation from physics (yaw from physics, pitch/roll from terrain)
        this.mesh.quaternion.set(pose.q.x, pose.q.y, pose.q.z, pose.q.w);

        // Apply terrain slope alignment (pitch and roll from terrain normal)
        this.alignToTerrainSlope();

        // Update wheel rotations based on velocity
        this.updateWheelRotations();

        // Update internal velocity state for speed calculations
        const vel = this.getLinearVelocity();
        this.velocity.copy(vel);
    }

    /**
     * Align the bus mesh rotation to follow the terrain slope.
     * Samples terrain heights at front/back and left/right of the bus
     * to compute pitch and roll, preserving the yaw from physics.
     */
    alignToTerrainSlope() {
        if (!this.mesh) return;

        const heightMap = this.scene?.userData?.terrainHeightMap;
        if (!heightMap) return;

        const pos = this.mesh.position;

        // Extract the current yaw (heading) from the mesh quaternion
        const currentEuler = new THREE.Euler().setFromQuaternion(this.mesh.quaternion, 'YXZ');
        const yaw = currentEuler.y;

        // Get the bus forward and right directions (flattened to XZ plane)
        const forwardDir = new THREE.Vector3(0, 0, -1).applyAxisAngle(new THREE.Vector3(0, 1, 0), yaw);
        const rightDir = new THREE.Vector3(1, 0, 0).applyAxisAngle(new THREE.Vector3(0, 1, 0), yaw);

        // Sample terrain heights at front and back of the bus (half-length)
        const halfLength = this.length / 2;
        const halfWidth = this.width / 2;

        const frontPos = new THREE.Vector3(
            pos.x + forwardDir.x * halfLength,
            0,
            pos.z + forwardDir.z * halfLength
        );
        const backPos = new THREE.Vector3(
            pos.x - forwardDir.x * halfLength,
            0,
            pos.z - forwardDir.z * halfLength
        );
        const leftPos = new THREE.Vector3(
            pos.x - rightDir.x * halfWidth,
            0,
            pos.z - rightDir.z * halfWidth
        );
        const rightPos = new THREE.Vector3(
            pos.x + rightDir.x * halfWidth,
            0,
            pos.z + rightDir.z * halfWidth
        );

        const hFront = heightMap.getHeight(frontPos.x, frontPos.z);
        const hBack = heightMap.getHeight(backPos.x, backPos.z);
        const hLeft = heightMap.getHeight(leftPos.x, leftPos.z);
        const hRight = heightMap.getHeight(rightPos.x, rightPos.z);

        // Calculate pitch angle from front-to-back height difference
        // Positive pitch = nose up (going uphill in forward direction)
        const pitchAngle = Math.atan2(hBack - hFront, this.length);

        // Calculate roll angle from left-to-right height difference
        // Positive roll = tilting right
        const rollAngle = Math.atan2(hRight - hLeft, this.width);

        // Build target quaternion: yaw from physics + pitch/roll from terrain
        this._targetSlopeQuaternion.setFromEuler(
            new THREE.Euler(pitchAngle, yaw, rollAngle, 'YXZ')
        );

        // Smoothly interpolate between current rotation and target
        this._currentSlopeQuaternion.copy(this.mesh.quaternion);
        this._currentSlopeQuaternion.slerp(this._targetSlopeQuaternion, this._slopeLerpFactor);

        // Apply the blended rotation
        this.mesh.quaternion.copy(this._currentSlopeQuaternion);
    }

    updateWheelRotations() {
        // Skip if we don't have wheels
        if (!this.wheels || this.wheels.length === 0) {
            //console.log("No wheels to update rotation");
            return;
        }

        // Get linear velocity
        const velocity = this.getLinearVelocity();

        // Calculate speed
        const speed = velocity.length();

        // Calculate wheel rotation speed based on vehicle speed
        const rotationSpeed = speed / this.wheelRadius;

        // Calculate direction vector to determine forward/backward rotation
        // Using -Z as forward direction (driving into the screen)
        const busForward = new THREE.Vector3(0, 0, -1).applyQuaternion(this.mesh.quaternion);
        const direction = velocity.dot(busForward) >= 0 ? 1 : -1;

        // Update wheel rotations
        for (let i = 0; i < this.wheels.length; i++) {
            if (this.wheels[i]) {
                // Apply steering angle to front wheels (first two wheels)
                if (i < 2) {
                    // For the standard orientation, steering is around Y axis
                    this.wheels[i].rotation.y = this.steering;
                }

                // Apply rotation based on speed and direction around the X axis 
                // (wheels roll around X axis when driving along Z axis)
                this.wheels[i].rotation.x += rotationSpeed * direction * 0.05;
            }
        }
    }

    // Helper method to smoothly interpolate values
    smoothValue(current, target, rate, deltaTime) {
        // Calculate factor based on deltaTime for consistent behavior regardless of frame rate
        const factor = Math.min(1, rate * deltaTime * 60);
        return current + (target - current) * factor;
    }

    // Extract common forces to a separate method to avoid duplication
    applyCommonForces(currentVelocity, speed) {
        // Apply drag force (air resistance) that increases with velocity squared
        if (speed > 1) {
            // Use a lower drag factor for better handling
            let dragFactor = 0.008; // Reduced from 0.01 for higher top speed
            if (speed > 30) {
                dragFactor = 0.01; // Reduced from 0.05 for better top speed
            }

            const dragForce = currentVelocity.clone().negate().multiplyScalar(
                dragFactor * currentVelocity.lengthSq()
            );
            this.addForce(dragForce);
        }

        // Apply angular drag to prevent excessive spinning
        try {
            // Get the angular velocity and apply drag if it's provided by the physics engine
            if (typeof this.physicsBody.getAngularVelocity === 'function') {
                const angularVelocity = this.getAngularVelocity();
                // Convert to THREE.Vector3 to ensure lengthSq is available
                const angVelVector = new THREE.Vector3(
                    angularVelocity.x || 0,
                    angularVelocity.y || 0,
                    angularVelocity.z || 0
                );

                if (angVelVector.lengthSq() > 0.01) {
                    // Lower angular drag so steering feels less sluggish
                    const angularDrag = angVelVector.clone().negate().multiplyScalar(0.1);
                    this.addTorque(angularDrag);
                }
            } else {
                // Fallback for when getAngularVelocity is not available
                if (this.mesh && Math.abs(this.mesh.rotation.z) > 0.1) {
                    // Apply a corrective torque to level the bus if it's tilting
                    const correction = new THREE.Vector3(0, 0, -this.mesh.rotation.z * 10);
                    this.addTorque(correction);
                }
            }
        } catch (error) {
            console.warn("Angular drag application error:", error.message);
        }
    }

    // Method for standard driving forces calculation
    applyStandardDrivingForces(deltaTime, forward, currentVelocity, speed, speedKmh, forceFactor = 1.0) {
        // Calculate velocity direction relative to bus orientation
        const velocityDir = currentVelocity.clone().normalize();
        const forwardAmount = forward.dot(velocityDir);

        // Check if we're moving forward or backward
        const movingForward = forwardAmount > 0;

        // Enforce the maximum speed limit (now 300 km/h)
        if (Math.abs(speed) > this.maxSpeed) {
            const limitedVelocity = currentVelocity.clone().normalize().multiplyScalar(this.maxSpeed);
            this.setLinearVelocity(limitedVelocity);
            // Update our local reference after limiting
            currentVelocity.copy(limitedVelocity);
            speed = Math.min(Math.abs(speed), this.maxSpeed) * Math.sign(speed);
            speedKmh = speed * 3.6;
        }

        // Apply force based on throttle/brake input
        if (this.throttle > 0) {
            // Forward movement - Calculate base engine force
            const maxForce = this.enginePower * 90 * forceFactor; // Increased multiplier from 60 to 90 for even better acceleration
            let engineForce = forward.clone().multiplyScalar(this.throttle * maxForce);

            // Speed limit handling
            const forwardSpeedLimitMs = this.maxSpeed; // Use the bus's maximum speed instead of road limit

            // No throttle reduction when approaching speed limit - full power at all speeds

            // Provide additional force at mid-range speeds to overcome drag
            if (speedKmh > 90 && speedKmh < 300) {
                const midRangeBoost = 1.5; // Increased from 1.2 to 1.5 for better mid-range acceleration
                engineForce.multiplyScalar(midRangeBoost);
            }

            // Provide moderate force at lower speeds to prevent getting stuck
            if (speed < 6 && this.throttle > 0.1) { // Reduced from 8
                // More boosted start
                const startBoost = Math.max(0, 6 - speed) * 10; // Increased from 3 to 10
                engineForce.multiplyScalar(1 + startBoost);

                // Add stronger upward component to help climb over obstacles
                if (speed < 3 && this.throttle > 0.5) {
                    const climbAssist = new THREE.Vector3(0, 10, 0); // Increased from 3 to 10
                    engineForce.add(climbAssist);
                }

                if (this.debug) {
                    console.log(`Applying terrain boost: ${startBoost.toFixed(2)}x`);
                }
            }

            // Apply the force
            this.addForce(engineForce);
        }
        else if (this.brake > 0) {
            if (movingForward || Math.abs(speed) < 0.5) {
                // First handle case when we're moving forward or nearly stopped
                // Calculate reverse force
                const reverseForce = forward.clone().negate().multiplyScalar(this.brake * this.reverseEnginePower * 40 * forceFactor);

                // Apply extra boost for starting in reverse
                if (Math.abs(speed) < 1) {
                    const startBoost = 2.5; // Boost factor for starting in reverse
                    reverseForce.multiplyScalar(startBoost);

                    // Add upward component for obstacles
                    if (this.brake > 0.5) {
                        reverseForce.add(new THREE.Vector3(0, 8, 0));
                    }
                }

                // Apply the reverse force
                this.addForce(reverseForce);

                // If moving forward quickly, also apply braking force

                if (movingForward && speed > 2) {
                    const brakeDir = currentVelocity.clone().normalize().negate();
                    const dynamicBrakePower = this.brakePower * (1 + Math.min(speed / 10, 3));
                    const brakeForce = brakeDir.multiplyScalar(this.brake * dynamicBrakePower * forceFactor);
                    this.addForce(brakeForce);
                }
            }
            else {
                // Already moving backward, continue applying reverse force
                const reverseForce = forward.clone().negate().multiplyScalar(this.brake * this.reverseEnginePower * 50 * forceFactor);
                this.addForce(reverseForce);

                // Add moderate upward component for reverse on obstacles
                if (this.brake > 0.5) {
                    const reverseClimbAssist = new THREE.Vector3(0, 5, 0);
                    this.addForce(reverseClimbAssist);
                }
            }
        }

        // Apply steering as torque
        if (Math.abs(this.steering) > 0.01) {
            // Gentler speed reduction — still responsive at high speed
            const steeringFactor = Math.max(0.5, 1.0 - (Math.abs(speed) / 50));

            // Stronger steering torque for responsive feel
            const steeringTorque = new THREE.Vector3(0, 1, 0).multiplyScalar(
                this.steering * steeringFactor * this.turnSpeed * 55 * deltaTime * forceFactor
            );
            this.addTorque(steeringTorque);
        }

        // Moderate downforce for stability without making turns sluggish
        const downforce = new THREE.Vector3(0, -9.81 * 2.0 * forceFactor, 0);
        this.addForce(downforce);
    }
}
